import { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertCircle, Info, RefreshCw } from 'lucide-react';

interface EnvioAEAT {
  id: string;
  timestamp: string;
  entorno: 'pruebas' | 'produccion';
  numeroFacturas: number;
  facturasEnviadas: string[];
  codigoEstado: string;
  success: boolean;
  csv?: string;
  tipoEnvio?: string;
  errores?: Array<{
    codigo: string;
    descripcion: string;
    numeroRegistro?: number;
  }>;
}

export function VerifactuEnvios() {
  const { fetchWithAuth } = useUser();
  const [envios, setEnvios] = useState<EnvioAEAT[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadEnvios = async () => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/api/verifactu/envios');
      if (!res.ok) {
        throw new Error('Error al cargar envíos');
      }
      const data = await res.json();
      setEnvios(data.envios || []);
    } catch (error) {
      toast.error('Error al cargar envíos', {
        description: error instanceof Error ? error.message : 'Error desconocido',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEnvios();
  }, []);

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleString('es-ES', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const getTipoEnvioBadge = (tipo?: string) => {
    if (!tipo) return null;

    const tipos: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
      'manual': { label: 'Manual', variant: 'default' },
      'automatico': { label: 'Automático', variant: 'secondary' },
      'programado': { label: 'Programado', variant: 'secondary' },
      'reintento_automatico': { label: 'Reintento', variant: 'outline' },
      'anulacion': { label: 'Anulación', variant: 'outline' },
    };

    const config = tipos[tipo] || { label: tipo, variant: 'default' };
    return <Badge variant={config.variant} className="text-xs">{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Historial de Envíos</h2>
          <p className="text-sm text-muted-foreground">
            Registro de envíos de facturas y rectificativas a la AEAT
          </p>
        </div>
        <Button variant="outline" onClick={loadEnvios}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Actualizar
        </Button>
      </div>

      {envios.length === 0 ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No hay envíos registrados todavía. Los envíos a AEAT aparecerán aquí automáticamente.
          </AlertDescription>
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Envíos Registrados</CardTitle>
            <CardDescription>
              Total de {envios.length} envío{envios.length !== 1 ? 's' : ''} registrado{envios.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Entorno</TableHead>
                  <TableHead className="text-center">Facturas</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>CSV</TableHead>
                  <TableHead>Errores</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {envios.map((envio) => (
                  <TableRow key={envio.id}>
                    <TableCell className="text-sm">
                      {formatDate(envio.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={envio.entorno === 'produccion' ? 'default' : 'secondary'}>
                        {envio.entorno === 'produccion' ? 'Producción' : 'Pruebas'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      {envio.numeroFacturas}
                    </TableCell>
                    <TableCell>
                      {getTipoEnvioBadge(envio.tipoEnvio)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {envio.success ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-600 font-medium">Exitoso</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm text-red-600 font-medium">Fallido</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {envio.csv ? (
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                          {envio.csv}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {envio.errores && envio.errores.length > 0 ? (
                        <div className="space-y-1">
                          {envio.errores.slice(0, 2).map((error, idx) => (
                            <div key={idx} className="text-xs text-red-600">
                              <span className="font-mono">{error.codigo}</span>: {error.descripcion}
                            </div>
                          ))}
                          {envio.errores.length > 2 && (
                            <span className="text-xs text-muted-foreground">
                              +{envio.errores.length - 2} más
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
